import React, { useRef, useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

// Constants
import TrackingConstants from 'Shared/constants/Tracking.constants';

// Type
import { CalendarMeeting } from 'Shared/types/CalendarMeeting.type';
import { ItemType } from 'Shared/types/MeetingItem.type';

// Hooks
import useElementDimensions from 'Wpds/hooks/useElementDimensions';
import { useToggle } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useCalendarMeetingsTracking } from 'Shared/tracking/use-calendar-meetings-tracking';
import { useTranslation } from 'Shared/contexts/TranslationsContext';

// Utils
import { formatDate } from 'Shared/utils/DateTime.utils';
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';
import { handleLaunchExternalLink } from 'Shared/utils/external-link';

// Selectors
import { getCalendarMeetingById } from 'Shared/selectors/CalendarMeetings.selectors';

// Components
import { WpButton, WpButtonGroup, WpButtonDropdown } from 'Wpds/components/button';
import { WpPopover, WpPopoverToggle, WpPopoverContent } from 'Wpds/components/popover';
import { WpDropdownMenu, WpDropdownMenuItem } from 'Wpds/components/dropdown';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import WpTextTruncate from 'Wpds/components/text-truncate/WpTextTruncate';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpDivider from 'Wpds/components/divider/WpDivider';
import CreateWorkspaceModal from 'Shared/components/CreateWorkspaceModal/CreateWorkspaceModal';
import AddGroupModalWithCalendarIntegration from 'Shared/components/modals/AddGroupModalWithCalendarIntegration/AddGroupModalWithCalendarIntegration';
import CreateMoreWorkspacesModal from 'Shared/components/modals/CreateMoreWorkspacesModal/CreateMoreWorkspacesModal';
import DayEventAttendees from './_partials/DayEventAttendees';
import CreateAiOrDtModal from 'Shared/components/CreateDtOrAiModal/CreateDtOrAiModal';
import DayEventConferenceUrl from './_partials/DayEventConferenceUrl';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';
import DayEventSection from './_partials/DayEventSection';
import ConnectCalendarEventToWorkspaceModal from 'Shared/components/ConnectCalendarEventToWorkspaceModal/ConnectCalendarEventToWorkspaceModal';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

// Styles
import './DayEvent.scss';

type Props = {
  event?: CalendarMeeting;
};

const DayEvent = ({ event }: Props) => {
  const ref = useRef();
  const history = useHistory();
  const { user } = useCurrentUser();
  const { tr } = useTranslation();

  const {
    trackClickedCalendarMeeting,
    trackClickedJoinVideoLink,
    trackViewInCalendar,
    trackClickedJoinMeetingMode,
    trackViewWorkspace,
    trackCickedToCreateWorkspace,
  } = useCalendarMeetingsTracking({
    calendarMeeting: event,
  });

  // const workspace = useSelector(state => getWorkspaceByCalendarMeetingId(state, { calendarMeetingId: event.id }));
  const meeting = useSelector(state => getCalendarMeetingById(state, { id: event.id }));
  const members = useSelector(state => state.members);

  const { width } = useElementDimensions({ elementRef: ref });
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isCreateAgendaOpen, toggleIsCreateAgendaOpen] = useToggle(false);
  const [isOneOnOneCreateModalOpen, toggleIsOneOnOneCreateModalOpen] = useToggle(false);
  const [isCreateGroupWorkspaceOpen, toggleIsCreateGroupWorkspaceOpen] = useToggle(false);
  const [isCreateDtModalOpen, toggleIsCreateDtModalOpen] = useToggle(false);
  const [isLinkToExistingModalOpen, toggleIsLinkToExistingModalOpen] = useToggle(false);

  const isAttachedToWorkspace = Boolean(meeting.workspaceId);

  const baseClassName = 'DayEvent';
  const classes = classnames(baseClassName, isPopoverOpen ? `${baseClassName}--open` : '');

  const startTime = formatDate(meeting.startTime, 'h:mm a');
  const endTime = formatDate(meeting.endTime, 'h:mm a');

  const otherUserEmail = meeting.attendees.find(a => a !== user.email);

  const toggleIsPopoverOpen = () => setIsPopoverOpen(!isPopoverOpen);

  const handleViwOnCalendarClick = () => {
    trackViewInCalendar();
    handleLaunchExternalLink({ href: meeting.htmlLink });
  };

  const handleViewWorkspaceClick = () => {
    trackViewWorkspace();
    history.push(PageUrlsUtils.makeWorkspaceUrl(meeting.workspaceId));
  };

  const handleTakeNotesClick = () => {
    trackClickedJoinMeetingMode();
    history.push(PageUrlsUtils.makeWorkspaceMeetingModeUrl(meeting.workspaceId));
  };
  const handleCreateAgendaClick = () => {
    setIsPopoverOpen(false);
    trackCickedToCreateWorkspace();
    if (event.attendees.length === 2) {
      toggleIsCreateAgendaOpen();
    } else {
      toggleIsCreateGroupWorkspaceOpen();
    }
  };

  useEffect(() => {
    if (isPopoverOpen) trackClickedCalendarMeeting();
  }, [isPopoverOpen]);

  const DiscussionTopics = () => {
    const DtsLength = event.discussionTopics.length;
    if (DtsLength > 0)
      return (
        <DayEventSection
          icon="comment-alt-check"
          title={`You're leading ${DtsLength} topic${DtsLength > 1 ? 's' : ''}`}
          subTitle="Prepare for the meeting"
          onClick={handleViewWorkspaceClick}
          postfixIcon="angle-right"
        />
      );

    return null;
  };

  return (
    <>
      <WpPopover
        open={isPopoverOpen}
        showArrow={false}
        placement="left-start"
        withBoxShadow
        onOpenChange={setIsPopoverOpen}
      >
        <WpPopoverToggle>
          <WpContainer className={classes} ref={ref}>
            <WpContainerBody padding={'05'}>
              <div className={`${baseClassName}__header`}>
                <div className={`${baseClassName}__header__details`}>
                  <h5 className={`${baseClassName}__header__details__title`}>
                    <WpTextTruncate text={meeting.name} numberOfLines={2} />
                  </h5>
                  <p className={`${baseClassName}__header__details__time`}>
                    {startTime} - {endTime}
                  </p>
                </div>
                {/* {!isAttachedToWorkspace && width > 240 && <CreateAgendaButton />} */}
                {!isAttachedToWorkspace && width > 240 && (
                  <div className={`${baseClassName}__header__buttons`} onClick={e => e.stopPropagation()}>
                    <WpButtonDropdown
                      variant="tertiary"
                      size="sm"
                      onClick={handleCreateAgendaClick}
                      block
                      addOnDropdownMenu={
                        <WpDropdownMenu>
                          <WpDropdownMenuItem onClick={handleCreateAgendaClick}>
                            <WpIcon name="plus-circle" />
                            Create new agenda
                          </WpDropdownMenuItem>
                          <WpDropdownMenuItem onClick={toggleIsLinkToExistingModalOpen}>
                            <WpIcon name="link" />
                            Link to existing agenda
                          </WpDropdownMenuItem>
                        </WpDropdownMenu>
                      }
                    >
                      <WpIcon name="plus-circle" />
                      Add Agenda
                    </WpButtonDropdown>
                  </div>
                )}
              </div>
            </WpContainerBody>
          </WpContainer>
        </WpPopoverToggle>
        <WpPopoverContent>
          <div className={`${baseClassName}__popover-content`}>
            <div className={`${baseClassName}__popover-content__toolbar`}>
              <WpButtonGroup spacing="sm">
                {event.htmlLink && (
                  <WpTooltip placement="top">
                    <WpTooltipTrigger>
                      <WpButton rounded variant="tertiary" onClick={handleViwOnCalendarClick}>
                        <WpIcon name="external-link" />
                      </WpButton>
                    </WpTooltipTrigger>
                    <WpTooltipContent>{tr('perform.calendar_meetings.day_event.view_in_calendar')}</WpTooltipContent>
                  </WpTooltip>
                )}

                <WpTooltip placement="top">
                  <WpTooltipTrigger>
                    <WpButton rounded variant="tertiary" onClick={toggleIsPopoverOpen}>
                      <WpIcon name="times" />
                    </WpButton>
                  </WpTooltipTrigger>
                  <WpTooltipContent>{tr('perform.calendar_meetings.day_event.close')}</WpTooltipContent>
                </WpTooltip>
              </WpButtonGroup>
            </div>
            <div className={`${baseClassName}__popover-content__header`}>
              <h5>{meeting.name}</h5>
              <p className={`${baseClassName}__popover-content__header__time`}>
                {startTime} - {endTime}
              </p>
              <div className={`${baseClassName}__popover-content__header__participants-join`}>
                <DayEventAttendees event={meeting} members={members} />
                <DayEventConferenceUrl event={meeting} onClick={trackClickedJoinVideoLink} />
              </div>
            </div>
            {!isAttachedToWorkspace && (
              <div className={`${baseClassName}__popover-content__empty-container`}>
                <EmptyStateContainer
                  subTitle="There isn’t a workspace for this meeting yet. Create one now!"
                  button={
                    <div onClick={e => e.stopPropagation()}>
                      <WpButtonDropdown
                        variant="primary"
                        size="lg"
                        onClick={handleCreateAgendaClick}
                        block
                        addOnDropdownMenu={
                          <WpDropdownMenu>
                            <WpDropdownMenuItem onClick={handleCreateAgendaClick}>
                              <WpIcon name="plus-circle" />
                              Create new agenda
                            </WpDropdownMenuItem>
                            <WpDropdownMenuItem onClick={toggleIsLinkToExistingModalOpen}>
                              <WpIcon name="link" />
                              Link to existing agenda
                            </WpDropdownMenuItem>
                          </WpDropdownMenu>
                        }
                      >
                        <WpIcon name="plus-circle" />
                        Add Agenda
                      </WpButtonDropdown>
                    </div>
                  }
                  inContainer
                  padding="05"
                />
              </div>
            )}
            {isAttachedToWorkspace && (
              <>
                <WpDivider padding={24} />
                <div className={`${baseClassName}__popover-content__body`}>
                  <DayEventSection
                    icon="file-signature"
                    title="Take meeting notes"
                    onClick={handleTakeNotesClick}
                    postfixIcon="angle-right"
                    subTitle="Capture notes and action items in meeting mode"
                  />
                  <DiscussionTopics />
                </div>
                <div className={`${baseClassName}__popover-content__footer`}>
                  {isAttachedToWorkspace && (
                    <WpButtonGroup spacing="md">
                      <WpButton size="lg" block onClick={handleViewWorkspaceClick}>
                        View Workspace
                      </WpButton>
                      <WpButton onClick={toggleIsCreateDtModalOpen} block size="lg" variant="secondary" color="gray">
                        <WpIcon name="plus" />
                        Create Topic
                      </WpButton>
                    </WpButtonGroup>
                  )}
                </div>
              </>
            )}
          </div>
        </WpPopoverContent>
      </WpPopover>
      {isCreateAgendaOpen && (
        <CreateWorkspaceModal
          isOpen={isCreateAgendaOpen}
          toggleModal={toggleIsCreateAgendaOpen}
          onSelectGroup={toggleIsCreateGroupWorkspaceOpen}
          onSelectOneOnOne={toggleIsOneOnOneCreateModalOpen}
        />
      )}
      {isCreateGroupWorkspaceOpen && (
        <AddGroupModalWithCalendarIntegration
          onClose={toggleIsCreateGroupWorkspaceOpen}
          defaultWorkspaceName={meeting.name}
          defaultRecurringMeetingId={meeting.recurringMeetingId}
          defaultInviteeEmails={meeting.attendees.map(attendee => {
            return { value: attendee, label: attendee, fromCalendar: true };
          })}
          includeSuggestions={false}
          calendarMeetingId={meeting.id}
          inviteMethod={TrackingConstants.INVITE_METHOD_TYPES.INVITE_FROM_CALENDAR_WIDGET}
        />
      )}
      {isOneOnOneCreateModalOpen && (
        <CreateMoreWorkspacesModal
          onClose={toggleIsOneOnOneCreateModalOpen}
          includeSuggestions={false}
          inviteMethod={TrackingConstants.INVITE_METHOD_TYPES.INVITE_FROM_CALENDAR_WIDGET}
          defaultInvitees={[
            {
              fullName: '',
              email: otherUserEmail,
              fromCalendar: true,
              recurringMeetingId: event.recurringMeetingId,
              calendarMeetingId: event.id,
            },
          ]}
        />
      )}
      {isCreateDtModalOpen && (
        <CreateAiOrDtModal
          isOpen={isCreateDtModalOpen}
          toggleIsOpen={toggleIsCreateDtModalOpen}
          itemType={ItemType.discussionTopic}
          workspaceUuid={meeting.workspaceId}
        />
      )}
      {isLinkToExistingModalOpen && (
        <ConnectCalendarEventToWorkspaceModal
          isOpen={isLinkToExistingModalOpen}
          toggleIsOpen={toggleIsLinkToExistingModalOpen}
          calendarMeeting={event}
        />
      )}
    </>
  );
};

export default DayEvent;
